import { CommonModule } from '@angular/common';
import { Component, computed, signal, Signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CheckPriceButtonComponent } from 'src/app/shared/components/check-price-button/check-price-button.component';
import { CopyTextButtonComponent } from 'src/app/shared/components/copy-text-button/copy-text-button.component';
import { SectionComponent } from 'src/app/shared/components/section/section.component';
import { SubsectionComponent } from 'src/app/shared/components/subsection/subsection.component';
import { CraftCalculatorStateService } from '../craft-calculator-state.service';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { OutputItem } from 'src/app/shared/models/craft-models/craft-models';
import { SparkLineChartComponent } from 'src/app/shared/components/spark-line-chart/spark-line-chart.component';
import { formatHoursAndMinutes } from 'src/app/shared/functions/datetime-functions';
import { SmallNumberPipe } from 'src/app/pipes/small-number/small-number.pipe';
import { ItemImageComponent } from 'src/app/shared/components/item-image/item-image.component';

@Component({
  selector: 'app-craft-calculator-prices-outputs',
  imports: [
    CommonModule,
    SectionComponent,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatTooltipModule,
    CheckPriceButtonComponent,
    CopyTextButtonComponent,
    SubsectionComponent,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    SparkLineChartComponent,
    SmallNumberPipe,
    ItemImageComponent,
  ],
  templateUrl: './craft-calculator-prices-outputs.component.html',
  styleUrl: './craft-calculator-prices-outputs.component.scss',
})
export class CraftCalculatorPricesOutputsComponent {
  public formatHoursAndMinutes = formatHoursAndMinutes;
  public searchText = signal('');
  public sort: Signal<MatSort | undefined> = viewChild<MatSort>('sort');
  public paginator: Signal<MatPaginator | undefined> = viewChild<MatPaginator>('paginator');
  public dataSource: Signal<MatTableDataSource<OutputItem>> = computed(() => {
    const ds = this.setupDataSource();
    this.craftCalculatorStateService.oldOutputSort = ds.sort;
    return ds;
  });
  public displayedColumns = computed(() => {
    if (this.craftCalculatorStateService.craftGroup().showCharts()) {
      return ['item', 'amount', 'location', 'pricetype', 'aodpprice', 'userprice', 'chart'];
    }
    return ['item', 'amount', 'location', 'pricetype', 'aodpprice', 'userprice'];
  });
  constructor(public craftCalculatorStateService: CraftCalculatorStateService) {}
  private setupDataSource(): MatTableDataSource<OutputItem> {
    let outputs = this.craftCalculatorStateService.craftGroup().outputItems();

    if (this.craftCalculatorStateService.craftGroup().hideUnusedItems()) {
      outputs = outputs.filter((output) => {
        return output.totalAmount() > 0;
      });
    }

    if (this.craftCalculatorStateService.craftGroup().onlyMissingPricesItems()) {
      outputs = outputs.filter((output) => output.hasMissingPrices());
    }

    if (this.searchText().trim() !== '') {
      const searchText = this.searchText().toLowerCase().trim();
      outputs = outputs.filter((output) => {
        return output.item.localizedName.toLowerCase().includes(searchText);
      });
    }

    const ds = new MatTableDataSource<OutputItem>(outputs);

    const sort = this.sort();

    if (sort) {
      ds.sortingDataAccessor = (item: OutputItem, property) => {
        const defaultSort = item.item.groupUniqueName.toLowerCase() + '_' + item.item.tier + '_' + item.item.enchantmentLevel;
        switch (property) {
          case 'item':
            return defaultSort;
          case 'amount':
            return item.totalAmount();
          case 'location':
            return (item.sellLocation()?.displayName?.toLowerCase() || 'zzz_no_location') + '_' + defaultSort;
          case 'pricetype':
            return (item.sellPriceType()?.friendlyName?.toLowerCase() || 'zzz_no_pricetype') + '_' + defaultSort;
          case 'aodpprice':
            return item.aodpPrice() || 0;
          case 'userprice':
            return item.sellUserPrice() || 0;
          case 'chart':
            return item.sellUserPrice() || 0;
          default:
            return defaultSort;
        }
      };

      ds.sort = sort;

      if (this.craftCalculatorStateService.oldOutputSort) {
        if (ds.sort) {
          ds.sort.active = this.craftCalculatorStateService.oldOutputSort.active;
          ds.sort.direction = this.craftCalculatorStateService.oldOutputSort?.direction;
        }
      }
    }

    const paginator = this.paginator();

    if (paginator) {
      ds.paginator = paginator;
    }
    return ds;
  }
}
